import { SocketClient } from "cbcore-ts"
import { IS, nil, NO, UIBaseButton, UITextField, UITextView, UIView, YES } from "uicore-ts"
import { RHRecordedJSONSanitizer, RHRecordedJSONValue } from "./RHRecordedJSONSanitizer"


export type RHUsageEventKind =
    "route" |
    "control" |
    "text" |
    "socket" |
    "milestone" |
    "external-comment" |
    "assertion" |
    "error"

export interface RHUsageEvent {
    kind: RHUsageEventKind
    timestamp: number
    target?: string
    displayText?: string
    eventKey?: string
    text?: string
    route?: string
    sanitizedRoute?: string
    functionName?: string
    status?: "started" | "succeeded" | "failed"
    durationMs?: number
    message?: string
    externalTimelineID?: string
    externalCommentID?: string
    videoTimeMs?: number
    recordingTimeMs?: number
    externalDocumentURL?: string
    textSummary?: RHRecordedJSONValue
    request?: RHRecordedJSONValue
    response?: RHRecordedJSONValue
    error?: RHRecordedJSONValue
    isButton?: boolean
}

export interface RHUsageViewDescriptor {
    target: string
    elementID: string
    className: string
    text: string
    placeholder: string
    hidden: boolean
    enabled: boolean
}

export type RHUsageEventSink = (event: RHUsageEvent) => void


export class RHUsageEventRuntime {
    
    static _isInstalled = NO
    static _internalUsageSinkIsInstalled = NO
    static _eventSinks: RHUsageEventSink[] = []
    static _originalUIViewSendControlEventForKey?: typeof UIView.prototype.sendControlEventForKey
    static _originalUITextFieldTextDescriptor?: PropertyDescriptor
    static _wrappedSocketFunctionNames = new Set<string>()
    static _jsonSanitizer = new RHRecordedJSONSanitizer()
    
    static install() {
        
        if (RHUsageEventRuntime._isInstalled) {
            return
        }
        
        RHUsageEventRuntime._isInstalled = YES
        RHUsageEventRuntime._installControlEventSource()
        RHUsageEventRuntime._installTextFieldSource()
        RHUsageEventRuntime._installSocketSource()
        
        Object.assign(window, { RHUsageEventRuntime })
        
    }
    
    static addEventSink(eventSink: RHUsageEventSink) {
        
        if (RHUsageEventRuntime._eventSinks.includes(eventSink)) {
            return
        }
        
        RHUsageEventRuntime._eventSinks.push(eventSink)
        
    }
    
    static removeEventSink(eventSink: RHUsageEventSink) {
        
        RHUsageEventRuntime._eventSinks = RHUsageEventRuntime._eventSinks.filter(
            candidateEventSink => candidateEventSink !== eventSink
        )
        
    }
    
    static registerViewWithUsageIdentifier(view: UIView, usageIdentifier: string) {
        
        Reflect.set(view, "_rhUsageIdentifier", usageIdentifier)
        Reflect.set(view, "_rhTestIdentifier", usageIdentifier)
        view.viewHTMLElement.setAttribute("data-rh-usage-id", usageIdentifier)
        view.viewHTMLElement.setAttribute("data-rh-test-id", usageIdentifier)
        
    }
    
    static routeDidChange(route: string) {
        
        RHUsageEventRuntime.emitEvent({
            kind: "route",
            timestamp: Date.now(),
            route,
            sanitizedRoute: RHUsageEventRuntime.routeByAliasingIdentifiers(route)
        })
        
    }
    
    static registerMilestone(message: string) {
        
        RHUsageEventRuntime.emitEvent({
            kind: "milestone",
            timestamp: Date.now(),
            message
        })
        
    }
    
    static viewDescriptors(): RHUsageViewDescriptor[] {
        
        const descriptors: RHUsageViewDescriptor[] = []
        document.querySelectorAll("*").forEach(element => {
            const view = (element as HTMLElement).UIViewObject
            if (IS(view)) {
                descriptors.push(RHUsageEventRuntime.descriptorForView(view))
            }
        })
        return descriptors
        
    }
    
    static sendControlEventToTarget(target: string, eventKey: string) {
        
        const view = RHUsageEventRuntime.viewForTarget(target)
        if (!view) {
            throw new Error(`No UIView registered for semantic target "${target}".`)
        }
        
        view.sendControlEventForKey(eventKey)
        
    }
    
    static fillTargetWithText(target: string, text: string) {
        
        const view = RHUsageEventRuntime.viewForTarget(target)
        if (!view) {
            throw new Error(`No UIView registered for semantic target "${target}".`)
        }
        
        const textField = RHUsageEventRuntime.textFieldFromView(view)
        if (!textField) {
            throw new Error(`Semantic target "${target}" is not a UITextField or wrapper with a textField.`)
        }
        
        textField.text = text
        textField.sendControlEventForKey(UITextField.controlEvent.TextChange)
        
    }
    
    static resetSanitizedJSONSession() {
        
        RHUsageEventRuntime._jsonSanitizer.resetSessionAliases()
        
    }
    
    static sanitizedJSONValue<Value>(value: Value, valueRole: "request" | "response" | "error" | "text", functionName?: string) {
        
        return RHUsageEventRuntime._jsonSanitizer.sanitizedValue(value, {
            path: valueRole,
            valueRole,
            functionName
        })
        
    }
    
    static routeByAliasingIdentifiers(route: string) {
        
        return RHUsageEventRuntime._jsonSanitizer.routeByAliasingIdentifiers(route)
        
    }
    
    static emitEvent(event: RHUsageEvent) {
        
        RHUsageEventRuntime._eventSinks.slice().forEach(eventSink => eventSink(event))
        
    }
    
    static descriptorForView(view: UIView): RHUsageViewDescriptor {
        
        return {
            target: RHUsageEventRuntime.targetForView(view),
            elementID: view.elementID,
            className: view.constructor.name,
            text: RHUsageEventRuntime.textForView(view),
            placeholder: RHUsageEventRuntime.placeholderForView(view),
            hidden: view.hidden,
            enabled: view.enabled
        }
        
    }
    
    static viewForTarget(target: string): UIView | null {
        
        const matchingDescriptor = RHUsageEventRuntime.viewDescriptors().find(
            descriptor => descriptor.target === target
        )
        if (!matchingDescriptor) {
            return null
        }
        
        let matchingView: UIView | null = null
        document.querySelectorAll("*").forEach(element => {
            const view = (element as HTMLElement).UIViewObject
            if (IS(view) && RHUsageEventRuntime.targetForView(view) === matchingDescriptor.target) {
                matchingView = view
            }
        })
        return matchingView
        
    }
    
    // Views of every kind are asked for whichever of these properties they happen to have.
    static _valueOfViewProperty(view: UIView, propertyName: string): unknown {
        
        return propertyName in view ? Reflect.get(view, propertyName) : undefined
        
    }
    
    static textFieldFromView(view: UIView): UITextField | null {
        
        if (view instanceof UITextField) {
            return view
        }
        
        const wrappedTextField = RHUsageEventRuntime._valueOfViewProperty(view, "textField")
        if (wrappedTextField instanceof UITextField) {
            return wrappedTextField
        }
        
        return null
        
    }
    
    static targetForView(view: UIView): string {
        
        const usageIdentifier = RHUsageEventRuntime._valueOfViewProperty(view, "_rhUsageIdentifier")
        if (typeof usageIdentifier === "string" && usageIdentifier.length > 0) {
            return usageIdentifier
        }
        
        const testIdentifier = RHUsageEventRuntime._valueOfViewProperty(view, "_rhTestIdentifier")
        if (typeof testIdentifier === "string" && testIdentifier.length > 0) {
            return testIdentifier
        }
        
        const usageAttributeIdentifier = view.viewHTMLElement.getAttribute("data-rh-usage-id")
        if (typeof usageAttributeIdentifier === "string" && usageAttributeIdentifier.length > 0) {
            return usageAttributeIdentifier
        }
        
        const testAttributeIdentifier = view.viewHTMLElement.getAttribute("data-rh-test-id")
        if (typeof testAttributeIdentifier === "string" && testAttributeIdentifier.length > 0) {
            return testAttributeIdentifier
        }
        
        if (view.elementID.length > 0) {
            return view.elementID
        }
        
        return `${view.constructor.name}.${view._UIViewIndex}`
        
    }
    
    static textForView(view: UIView): string {
        
        const text = RHUsageEventRuntime._valueOfViewProperty(view, "text")
        if (typeof text === "string") {
            return text
        }
        
        const textContent = view.viewHTMLElement.textContent
        if (typeof textContent === "string") {
            return textContent
        }
        
        return ""
        
    }
    
    static placeholderForView(view: UIView): string {
        
        const placeholderText = RHUsageEventRuntime._valueOfViewProperty(view, "placeholderText")
        if (typeof placeholderText === "string") {
            return placeholderText
        }
        
        const textField = RHUsageEventRuntime.textFieldFromView(view)
        if (textField) {
            return textField.placeholderText
        }
        
        return ""
        
    }
    
    static displayTextForView(view: UIView): string {
        
        const titleLabel = RHUsageEventRuntime._valueOfViewProperty(view, "titleLabel")
        if (titleLabel instanceof UITextView) {
            const titleText = titleLabel.text
            if (typeof titleText === "string" && titleText.length > 0) {
                return titleText
            }
        }
        
        const viewText = RHUsageEventRuntime.textForView(view)
        if (viewText.length > 0) {
            return viewText
        }
        
        return RHUsageEventRuntime.targetForView(view)
        
    }
    
    static controlEventWillSend(view: UIView, eventKey: string) {
        
        RHUsageEventRuntime.emitEvent({
            kind: "control",
            timestamp: Date.now(),
            target: RHUsageEventRuntime.targetForView(view),
            displayText: RHUsageEventRuntime.displayTextForView(view),
            eventKey,
            isButton: view instanceof UIBaseButton
        })
        
    }
    
    static textFieldTextDidChange(view: UITextField, text: string) {
        
        const textSummary = RHUsageEventRuntime.sanitizedJSONValue(text, "text")
        const event: RHUsageEvent = {
            kind: "text",
            timestamp: Date.now(),
            target: RHUsageEventRuntime.targetForView(view),
            displayText: RHUsageEventRuntime.displayTextForView(view),
            textSummary
        }
        
        if (textSummary.kind === "string" && textSummary.redaction === "exact" && typeof textSummary.value === "string") {
            event.text = textSummary.value
        }
        
        RHUsageEventRuntime.emitEvent(event)
        
    }
    
    static socketFunctionWillStart<Request>(functionName: string, request: Request) {
        
        RHUsageEventRuntime.emitEvent({
            kind: "socket",
            timestamp: Date.now(),
            functionName,
            status: "started",
            request: RHUsageEventRuntime.sanitizedJSONValue(request, "request", functionName)
        })
        
    }
    
    static socketFunctionDidSucceed<Response>(functionName: string, response: Response, durationMs?: number) {
        
        RHUsageEventRuntime.emitEvent({
            kind: "socket",
            timestamp: Date.now(),
            functionName,
            status: "succeeded",
            durationMs,
            response: RHUsageEventRuntime.sanitizedJSONValue(response, "response", functionName)
        })
        
    }
    
    static socketFunctionDidFail(functionName: string, error: unknown, durationMs?: number) {
        
        RHUsageEventRuntime.emitEvent({
            kind: "socket",
            timestamp: Date.now(),
            functionName,
            status: "failed",
            durationMs,
            error: RHUsageEventRuntime.sanitizedJSONValue(error, "error", functionName)
        })
        
    }
    
    static _installControlEventSource() {
        
        RHUsageEventRuntime._originalUIViewSendControlEventForKey = UIView.prototype.sendControlEventForKey
        const originalSendControlEventForKey = RHUsageEventRuntime._originalUIViewSendControlEventForKey
        
        UIView.prototype.sendControlEventForKey = function (
            this: UIView,
            eventKey: string,
            nativeEvent?: Event
        ) {
            RHUsageEventRuntime.controlEventWillSend(this, eventKey)
            return originalSendControlEventForKey.call(this, eventKey, nativeEvent)
        }
        
    }
    
    static _installTextFieldSource() {
        
        RHUsageEventRuntime._originalUITextFieldTextDescriptor = Object.getOwnPropertyDescriptor(
            UITextField.prototype,
            "text"
        )
        const textDescriptor = RHUsageEventRuntime._originalUITextFieldTextDescriptor
        if (!textDescriptor || !textDescriptor.set || !textDescriptor.get) {
            return
        }
        
        const originalTextGetter = textDescriptor.get
        const originalTextSetter = textDescriptor.set
        
        Object.defineProperty(UITextField.prototype, "text", {
            get: function (this: UITextField) {
                return originalTextGetter.call(this)
            },
            set: function (this: UITextField, text: string) {
                const previousText = originalTextGetter.call(this)
                originalTextSetter.call(this, text)
                if (previousText === text) {
                    return
                }
                RHUsageEventRuntime.textFieldTextDidChange(this, text)
            }
        })
        
    }
    
    static _installSocketSource() {
        
        Object.keys(SocketClient).forEach(functionName => {
            const socketFunction = (SocketClient)[functionName]
            if (typeof socketFunction !== "function") {
                return
            }
            if (RHUsageEventRuntime._wrappedSocketFunctionNames.has(functionName)) {
                return
            }
            
            RHUsageEventRuntime._wrappedSocketFunctionNames.add(functionName)
            ;(SocketClient)[functionName] = async function (...parameters: unknown[]) {
                const startTimestamp = Date.now()
                RHUsageEventRuntime.socketFunctionWillStart(functionName, parameters[0])
                try {
                    const response = await socketFunction.apply(this, parameters)
                    RHUsageEventRuntime.socketFunctionDidSucceed(functionName, response, Date.now() - startTimestamp)
                    return response
                }
                catch (error) {
                    RHUsageEventRuntime.socketFunctionDidFail(functionName, error, Date.now() - startTimestamp)
                    throw error
                }
            }
        })
        
    }
    
    static installInternalUsageSink() {
        
        if (RHUsageEventRuntime._internalUsageSinkIsInstalled) {
            return
        }
        
        RHUsageEventRuntime._internalUsageSinkIsInstalled = YES
        RHUsageEventRuntime.addEventSink(event => RHUsageEventRuntime._persistInternalUsageEventIfNeeded(event))
        
    }
    
    static _persistInternalUsageEventIfNeeded(event: RHUsageEvent) {
        
        if (event.kind === "route" && typeof event.route === "string") {
            SocketClient.RouteDidChange(event.route).then(nil)
            return
        }
        
        if (!RHUsageEventRuntime._eventShouldBeTrackedAsButtonPress(event)) {
            return
        }
        
        const buttonLabel = event.displayText || event.target || ""
        if (buttonLabel.length > 0) {
            SocketClient.ButtonWasPressed(buttonLabel).then(nil)
        }
        
    }
    
    static _eventShouldBeTrackedAsButtonPress(event: RHUsageEvent): boolean {
        
        if (event.kind !== "control") {
            return NO
        }
        if (event.isButton !== YES) {
            return NO
        }
        
        return event.eventKey === UIBaseButton.controlEvent.PointerUpInside ||
            event.eventKey === UIBaseButton.controlEvent.EnterDown
        
    }
    
}
